import { RefundExternalKeysFilter } from 'app/src/features/Shopping/Refund/components/functions'
import {
  KindPosteEnergia,
  KindPosteFibra,
  requestedRefundFormatter,
} from 'app/src/features/Shopping/Refund/components/RefundFunctions'
import { FormItemEnum, REGEX_Importo, type FormPropsType } from 'app/src/features/Shopping/shared'
import type { refundApi } from 'app/src/services/welfareportal'
import { ErrorMessage, InformativeBanner } from 'app/src/shared/components'
import { borderLessClassNames } from 'app/src/shared/helpers'
import { useTranslation } from 'app/src/shared/hooks'
import { Calendar } from 'primereact/calendar'
import { Dropdown } from 'primereact/dropdown'
import { IconField } from 'primereact/iconfield'
import { InputIcon } from 'primereact/inputicon'
import { InputText } from 'primereact/inputtext'
import { useMemo } from 'react'

const updateInvoiceTotal = (props: FormPropsType, newValue: string) => {
  const fieldNames: string[] = ['invoiceTotal']
  const fieldValues: unknown[] = [newValue]
  const fieldTypes: FormItemEnum[] = [FormItemEnum.NUMBER]
  const validRegex = new RegExp(REGEX_Importo).test(newValue)
  let curValue: number | undefined = validRegex ? parseFloat(newValue?.replace(',', '.')) : undefined

  //check subtract
  if (
    props.refund?.rule?.externalKeys &&
    props.refund.rule.externalKeys.some((key) => RefundExternalKeysFilter(key, props.refund)) &&
    validRegex
  ) {
    props.refund.rule.externalKeys
      .filter((key) => RefundExternalKeysFilter(key, props.refund))
      .forEach((extKey) => {
        if (
          new RegExp(REGEX_Importo).test(props.formData?.[extKey.key ?? '']) &&
          props.formData?.[`${extKey.key}Number`] &&
          curValue
        ) {
          curValue -= props.formData?.[`${extKey.key}Number`]
        }
      })
  }

  if (validRegex && curValue) {
    fieldNames.push('requestedRefund')
    fieldValues.push(requestedRefundFormatter.format(curValue < 0 ? 0 : curValue))
    fieldTypes.push(FormItemEnum.NUMBER)
  }

  props.setBulkFormData(fieldNames, fieldValues, fieldTypes)
}

const updateExternalKeyField = (props: FormPropsType, fieldKey: string, newValue: string) => {
  const fieldNames: string[] = [fieldKey]
  const fieldValues: unknown[] = [newValue]
  const fieldTypes: FormItemEnum[] = [FormItemEnum.NUMBER]

  const validRegex = new RegExp(REGEX_Importo).test(newValue)

  if (
    props.refund?.rule?.externalKeys &&
    props.refund.rule.externalKeys.some((key) => RefundExternalKeysFilter(key, props.refund)) &&
    validRegex &&
    new RegExp(REGEX_Importo).test(props.formData['invoiceTotal'])
  ) {
    let requestedRefund = props.formData['invoiceTotalNumber'] - parseFloat(newValue?.replace(',', '.'))
    props.refund.rule.externalKeys
      .filter((keyItem: refundApi.AdditionalKey) => RefundExternalKeysFilter(keyItem, props.refund) && keyItem.key != fieldKey)
      .forEach((extKey: refundApi.AdditionalKey) => {
        if (new RegExp(REGEX_Importo).test(props.formData?.[extKey.key ?? '']) && props.formData?.[`${extKey.key}Number`]) {
          requestedRefund -= props.formData?.[`${extKey.key}Number`]
        }
      })
    fieldNames.push('requestedRefund')
    fieldValues.push(requestedRefundFormatter.format(requestedRefund < 0 ? 0 : requestedRefund))

    fieldTypes.push(FormItemEnum.NUMBER)
  }

  props.setBulkFormData(fieldNames, fieldValues, fieldTypes)
}

function MultiVoucherUsageInvoiceFormPosteEnergia(
  props: FormPropsType & { paymentOptions: { key: string; text: string }[]; paymentOptionsPlaceholder: string },
) {
  const { t } = useTranslation('shopping', { keyPrefix: 'Direct.MultiVoucher' })

  const contractIdInfo = useMemo(() => {
    return t('Usage.Kinds')[props.refund.kind].SubCategories![props.refund?.attributes?.subCategoryId ?? '']
  }, [t, props.refund])

  return (
    <div className='mx-auto mt-6 max-w-6xl'>
      {contractIdInfo.invoiceDisclaimer && <InformativeBanner inlineIcon text={contractIdInfo.invoiceDisclaimer} />}
      <div className='grid grid-cols-1 md:grid-cols-2'>
        <div className='col-span-1 my-[10px] grid grid-cols-1 md:mr-[20px]'>
          <label className='uppercase' htmlFor='domiciliation'>
            {t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.domiciliation')}
          </label>
          <Dropdown
            id='domiciliation'
            placeholder={props.paymentOptionsPlaceholder}
            className={borderLessClassNames}
            pt={{ root: { className: 'font-bold w-full' } }}
            value={
              props.formData?.domiciliation && props.paymentOptions.some((x) => x.key === props.formData.domiciliation)
                ? {
                    key: props.formData.domiciliation,
                    text: props.paymentOptions.find((x) => x.key === props.formData.domiciliation)?.text,
                  }
                : undefined
            }
            onChange={(event) => props.setFormData('domiciliation', event.value.key, FormItemEnum.STRING)}
            options={props.paymentOptions}
            optionLabel='text'
          ></Dropdown>
          <ErrorMessage errorMessage={props.formErrors?.domiciliation} />
        </div>
        <div className='col-span-1 my-[10px] grid grid-cols-1 md:mr-[20px]'>
          <label className='uppercase' htmlFor='invoiceId'>
            {t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.documentId')}
          </label>
          <InputText
            id='invoiceId'
            placeholder={t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.placeholder')}
            className={borderLessClassNames}
            pt={{ root: { className: 'font-bold w-full' } }}
            value={props.formData?.invoiceId}
            onChange={(event) => props.setFormData('invoiceId', event.currentTarget.value, FormItemEnum.STRING)}
          ></InputText>
          <ErrorMessage errorMessage={props.formErrors?.invoiceId} />
        </div>
      </div>
      <div className='grid grid-cols-1 md:grid-cols-2'>
        <div className='col-span-1 my-[10px] grid grid-cols-1 md:mr-[20px]'>
          <label className='uppercase' htmlFor='dueDate'>
            {t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.dueDate')}
          </label>
          <Calendar
            id='dueDate'
            value={props.formData?.dueDate ? new Date(props.formData?.dueDate) : undefined}
            readOnlyInput={false}
            pt={{ root: { className: borderLessClassNames } }}
            placeholder={t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.dateFormatPlaceholder')}
            dateFormat={t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.dateFormat')}
            onSelect={(ev) => props.setFormData('dueDate', ev.value, FormItemEnum.DATE)}
            showIcon
          />
          <ErrorMessage errorMessage={props.formErrors?.dueDate} />
        </div>
        <div className='col-span-1 my-[10px] grid grid-cols-1 md:mr-[20px]'>
          <label className='uppercase' htmlFor='customerId'>
            {t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.customerId')}
          </label>
          <InputText
            id='customerId'
            placeholder={t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.placeholder')}
            className={borderLessClassNames}
            pt={{ root: { className: 'font-bold w-full' } }}
            value={props.formData?.customerId}
            onChange={(event) => props.setFormData('customerId', event.currentTarget.value, FormItemEnum.STRING)}
          ></InputText>
          <ErrorMessage errorMessage={props.formErrors?.customerId} />
        </div>
      </div>
      <div className='grid grid-cols-1 md:grid-cols-2'>
        <div className='col-span-1 my-[10px] grid grid-cols-1 md:mr-[20px]'>
          <label className='uppercase' htmlFor='invoiceDate'>
            {t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.documentDate')}
          </label>
          <Calendar
            id='invoiceDate'
            value={props.formData?.invoiceDate ? new Date(props.formData?.invoiceDate) : undefined}
            readOnlyInput={false}
            pt={{ root: { className: borderLessClassNames } }}
            placeholder={t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.dateFormatPlaceholder')}
            dateFormat={t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.dateFormat')}
            onSelect={(ev) => props.setFormData('invoiceDate', ev.value, FormItemEnum.DATE)}
            maxDate={new Date()}
            showIcon
          />
          <ErrorMessage errorMessage={props.formErrors?.invoiceDate} />
        </div>
        <div className='col-span-1 my-[10px] grid grid-cols-1 md:mr-[20px]'>
          <label className='uppercase' htmlFor={contractIdInfo.contractIdFieldName}>
            {contractIdInfo.contractIdLabel}
          </label>
          <InputText
            id={contractIdInfo.contractIdFieldName}
            placeholder={t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.placeholder')}
            className={borderLessClassNames}
            pt={{ root: { className: 'font-bold w-full' } }}
            value={props.formData?.[contractIdInfo.contractIdFieldName]}
            onChange={(event) =>
              props.setFormData(contractIdInfo.contractIdFieldName, event.currentTarget.value, FormItemEnum.STRING)
            }
          ></InputText>
          <ErrorMessage errorMessage={props.formErrors?.[contractIdInfo.contractIdFieldName]} />
        </div>
      </div>
      {props.refund?.rule?.externalKeys &&
        props.refund.rule.externalKeys.some((key) => RefundExternalKeysFilter(key, props.refund)) && (
          <div className='grid grid-cols-1 md:grid-cols-2'>
            {props.refund.rule.externalKeys
              .filter((key) => RefundExternalKeysFilter(key, props.refund))
              .map((x) => (
                <div key={x.key} className='col-span-1 my-[10px] grid grid-cols-1 md:mr-[20px]'>
                  <label className='uppercase' htmlFor={x.key}>
                    {x.label}
                  </label>
                  <IconField iconPosition='left'>
                    <InputIcon className='pi pi-euro'> </InputIcon>
                    <InputText
                      id={x.key ?? ''}
                      placeholder={t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.placeholder')}
                      className={borderLessClassNames}
                      pt={{ root: { className: 'font-bold w-full' } }}
                      value={props.formData?.[x.key ?? '']}
                      onChange={(event) => updateExternalKeyField(props, x.key!, event.currentTarget.value)}
                    ></InputText>
                  </IconField>
                  <ErrorMessage errorMessage={props.formErrors?.[x.key ?? ''] ?? props.formErrors?.[x.key + 'Number']} />
                </div>
              ))}
          </div>
        )}
      <div className='grid grid-cols-1 md:grid-cols-2'>
        <div className='col-span-1 my-[10px] grid grid-cols-1 md:mr-[20px]'>
          <label className='uppercase' htmlFor='invoiceTotal'>
            {t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.invoiceTotal')}
          </label>
          <IconField iconPosition='left'>
            <InputIcon className='pi pi-euro'> </InputIcon>

            <InputText
              id='invoiceTotal'
              placeholder={t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.placeholder')}
              className={borderLessClassNames}
              pt={{ root: { className: 'font-bold w-full' } }}
              value={props.formData?.invoiceTotal}
              onChange={(event) => updateInvoiceTotal(props, event.currentTarget.value)}
            ></InputText>
          </IconField>
          <ErrorMessage errorMessage={props.formErrors?.invoiceTotal ?? props.formErrors?.invoiceTotalNumber} />
        </div>
        <div className='col-span-1 my-[10px] grid grid-cols-1 md:mr-[20px]'>
          <label className='uppercase' htmlFor='requestedRefundNumber'>
            {t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.requestedRefund')}
          </label>
          <IconField iconPosition='left'>
            <InputIcon className='pi pi-euro'> </InputIcon>
            <InputText
              id='requestedRefundNumber'
              placeholder={t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.placeholder')}
              className={borderLessClassNames}
              pt={{ root: { className: 'font-bold w-full' } }}
              value={props.formData?.requestedRefund}
              readOnly
              disabled
            ></InputText>
          </IconField>
          <ErrorMessage
            errorMessage={
              (props.formErrors?.invoiceTotal ?? props.formErrors?.invoiceTotalNumber)
                ? ''
                : (props.formErrors?.requestedRefund ??
                  props.formErrors?.requestedRefundNumber ??
                  props.formErrors?.welfareCreditNumber ??
                  props.formErrors?.[''])
            }
          />
        </div>
      </div>
    </div>
  )
}

function MultiVoucherUsageInvoiceFormPosteFibra(
  props: FormPropsType & { paymentOptions: { key: string; text: string }[]; paymentOptionsPlaceholder: string },
) {
  const { t } = useTranslation('shopping', { keyPrefix: 'Direct.MultiVoucher' })

  const contractIdInfo = useMemo(() => {
    return t('Usage.Kinds')[props.refund.kind].SubCategories![props.refund?.attributes?.subCategoryId ?? '']
  }, [t, props.refund])

  return (
    <div className='mx-auto mt-6 max-w-6xl'>
      {contractIdInfo.invoiceDisclaimer && <InformativeBanner inlineIcon text={contractIdInfo.invoiceDisclaimer} />}
      <div className='grid grid-cols-1 md:grid-cols-2'>
        <div className='col-span-1 my-[10px] grid grid-cols-1 md:mr-[20px]'>
          <label className='uppercase' htmlFor='domiciliation'>
            {t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.domiciliation')}
          </label>
          <Dropdown
            id='domiciliation'
            placeholder={props.paymentOptionsPlaceholder}
            className={borderLessClassNames}
            pt={{ root: { className: 'font-bold w-full' } }}
            value={
              props.formData?.domiciliation && props.paymentOptions.some((x) => x.key === props.formData.domiciliation)
                ? {
                    key: props.formData.domiciliation,
                    text: props.paymentOptions.find((x) => x.key === props.formData.domiciliation)?.text,
                  }
                : undefined
            }
            onChange={(event) => props.setFormData('domiciliation', event.value.key, FormItemEnum.STRING)}
            options={props.paymentOptions}
            optionLabel='text'
          ></Dropdown>
          <ErrorMessage errorMessage={props.formErrors?.domiciliation} />
        </div>
        <div className='col-span-1 my-[10px] grid grid-cols-1 md:mr-[20px]'>
          <label className='uppercase' htmlFor='invoiceId'>
            {t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.documentId')}
          </label>
          <InputText
            id='invoiceId'
            placeholder={t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.placeholder')}
            className={borderLessClassNames}
            pt={{ root: { className: 'font-bold w-full' } }}
            value={props.formData?.invoiceId}
            onChange={(event) => props.setFormData('invoiceId', event.currentTarget.value, FormItemEnum.STRING)}
          ></InputText>
          <ErrorMessage errorMessage={props.formErrors?.invoiceId} />
        </div>
      </div>
      <div className='grid grid-cols-1 md:grid-cols-2'>
        <div className='col-span-1 my-[10px] grid grid-cols-1 md:mr-[20px]'>
          <label className='uppercase' htmlFor='dueDate'>
            {t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.dueDate')}
          </label>
          <Calendar
            id='dueDate'
            value={props.formData?.dueDate ? new Date(props.formData?.dueDate) : undefined}
            readOnlyInput={false}
            pt={{ root: { className: borderLessClassNames } }}
            placeholder={t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.dateFormatPlaceholder')}
            dateFormat={t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.dateFormat')}
            onSelect={(ev) => props.setFormData('dueDate', ev.value, FormItemEnum.DATE)}
            showIcon
          />
          <ErrorMessage errorMessage={props.formErrors?.dueDate} />
        </div>
        <div className='col-span-1 my-[10px] grid grid-cols-1 md:mr-[20px]'>
          <label className='uppercase' htmlFor='providerId'>
            {t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.Fiber.providerId')}
          </label>
          <InputText
            id='providerId'
            placeholder={t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.placeholder')}
            className={borderLessClassNames}
            pt={{ root: { className: 'font-bold w-full' } }}
            value={props.formData?.providerId}
            onChange={(event) => props.setFormData('providerId', event.currentTarget.value, FormItemEnum.STRING)}
          ></InputText>
          <ErrorMessage errorMessage={props.formErrors?.providerId} />
        </div>
      </div>
      {props.refund?.rule?.externalKeys &&
        props.refund.rule.externalKeys.some((key) => RefundExternalKeysFilter(key, props.refund)) && (
          <div className='grid grid-cols-1 md:grid-cols-2'>
            {props.refund.rule.externalKeys
              .filter((key) => RefundExternalKeysFilter(key, props.refund))
              .map((x) => (
                <div key={x.key} className='col-span-1 my-[10px] grid grid-cols-1 md:mr-[20px]'>
                  <label className='uppercase' htmlFor={x.key}>
                    {x.label}
                  </label>
                  <IconField iconPosition='left'>
                    <InputIcon className='pi pi-euro'> </InputIcon>
                    <InputText
                      id={x.key ?? ''}
                      placeholder={t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.placeholder')}
                      className={borderLessClassNames}
                      pt={{ root: { className: 'font-bold w-full' } }}
                      value={props.formData?.[x.key ?? '']}
                      onChange={(event) => updateExternalKeyField(props, x.key!, event.currentTarget.value)}
                    ></InputText>
                  </IconField>
                  <ErrorMessage errorMessage={props.formErrors?.[x.key ?? ''] ?? props.formErrors?.[x.key + 'Number']} />
                </div>
              ))}
          </div>
        )}
      <div className='grid grid-cols-1 md:grid-cols-2'>
        <div className='col-span-1 my-[10px] grid grid-cols-1 md:mr-[20px]'>
          <label className='uppercase' htmlFor='invoiceTotal'>
            {t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.invoiceTotal')}
          </label>
          <IconField iconPosition='left'>
            <InputIcon className='pi pi-euro'> </InputIcon>

            <InputText
              id='invoiceTotal'
              placeholder={t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.placeholder')}
              className={borderLessClassNames}
              pt={{ root: { className: 'font-bold w-full' } }}
              value={props.formData?.invoiceTotal}
              onChange={(event) => updateInvoiceTotal(props, event.currentTarget.value)}
            ></InputText>
          </IconField>
          <ErrorMessage errorMessage={props.formErrors?.invoiceTotal ?? props.formErrors?.invoiceTotalNumber} />
        </div>
        <div className='col-span-1 my-[10px] grid grid-cols-1 md:mr-[20px]'>
          <label className='uppercase' htmlFor='requestedRefundNumber'>
            {t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.requestedRefund')}
          </label>
          <IconField iconPosition='left'>
            <InputIcon className='pi pi-euro'> </InputIcon>
            <InputText
              id='requestedRefundNumber'
              placeholder={t('Usage.Funnel.Steps.InvoiceUpdate.FormFields.Labels.placeholder')}
              className={borderLessClassNames}
              pt={{ root: { className: 'font-bold w-full' } }}
              value={props.formData?.requestedRefund}
              readOnly
            ></InputText>
          </IconField>
          <ErrorMessage
            errorMessage={
              (props.formErrors?.invoiceTotal ?? props.formErrors?.invoiceTotalNumber)
                ? ''
                : (props.formErrors?.requestedRefund ??
                  props.formErrors?.requestedRefundNumber ??
                  props.formErrors?.welfareCreditNumber ??
                  props.formErrors?.[''])
            }
          />
        </div>
      </div>
    </div>
  )
}

export function MultiVoucherUsageInvoiceForm(props: FormPropsType) {
  const { t } = useTranslation('shopping', { keyPrefix: 'Direct.MultiVoucher.Usage' })
  const PaymentMethodOptions: { key: string; text: string }[] = useMemo(
    () => t('Kinds')[props.refund?.kind].DomiciliationOptions?.map((value) => ({ ...value })),
    [t, props.refund],
  )
  const PaymentOptionsPlaceholder: string = useMemo(
    () => t('Kinds')[props.refund?.kind].domiciliationOptionsPlaceholder,
    [t, props.refund],
  )
  return (
    <>
      {props.refund?.kind === KindPosteEnergia ? (
        <MultiVoucherUsageInvoiceFormPosteEnergia
          {...props}
          paymentOptions={PaymentMethodOptions}
          paymentOptionsPlaceholder={PaymentOptionsPlaceholder}
        />
      ) : props.refund?.kind === KindPosteFibra ? (
        <MultiVoucherUsageInvoiceFormPosteFibra
          {...props}
          paymentOptions={PaymentMethodOptions}
          paymentOptionsPlaceholder={PaymentOptionsPlaceholder}
        />
      ) : (
        <></>
      )}
      {t('Funnel.Steps.InvoiceUpdate.FormFields.Labels.formTrailer') && (
        <div dangerouslySetInnerHTML={{ __html: t('Funnel.Steps.InvoiceUpdate.FormFields.Labels.formTrailer') }} />
      )}
    </>
  )
}
